import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  getPaginatedMeterReadings,
  createMeterReadings,
  updateMeterReadings,
  deleteMeterReadings,
} from '../api/meter-readingsAPI';
import { getMeters } from '../api/metersAPI';
import { getPaginatedAssets } from '../api/assetAPI';
import { getAssetMeter } from '../api/asset-meterAPI';
import { useTableFilters } from '../utils/useTableFilters';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import MeterReadingsDataTable from './MeterReadingsDataTable';
import { toLocalInputFormat, nowLocalIso } from '../utils/dateUtils';
import { validateMeterReading } from '../utils/useValidator';
import { handleSaveMeterReading, validateReadingEntry } from '../utils/meterReadingHelper';

const MeterReadingsLayer = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [readings, setReadings] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const listRef = useRef(null);
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const userProfile = useUserProfile();

  const [meterOptions, setMeterOptions] = useState([]);
  const [assetMeters, setAssetMeters] = useState([]);

  const [initialAssetOptions, setInitialAssetOptions] = useState([]);
  const [selectedAssetOption, setSelectedAssetOption] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newReading, setNewReading] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getMeters()
      .then(data => setMeterOptions(data || []))
      .catch(err => console.error('meters', err));
    getAssetMeter()
      .then(data => setAssetMeters(data || []))
      .catch(err => console.error('asset-meter', err));
  }, []);

  // Load the first page of assets for the dropdown
  useEffect(() => {
    if (!assetMeters.length) return;
    (async () => {
      try {
        const opts = await handleAssetSearch('');
        setInitialAssetOptions(opts);
      } catch (err) {
        console.error('initial assets', err);
      }
    })();
  }, [assetMeters]);

  const handleAssetSearch = async (inputValue) => {
    try {
      const shouldLoadDefault = !inputValue;
      if (!shouldLoadDefault && inputValue.length < 2) return [];
      const resp = await getPaginatedAssets({
        page: 1,
        pageSize: 10,
        search: shouldLoadDefault ? '' : inputValue,
      });
      const items = resp.data || resp;
      const allowedIds = new Set(assetMeters.map((am) => am.assetId));
      return items
        .filter((a) => allowedIds.has(a.id))
        .map((a) => ({ value: a.id, label: `${a.assetNumber} – ${a.assetDescription}` }));
    } catch (err) {
      console.error('Error fetching assets:', err);
      return [];
    }
  };

  const tableFilters = useMemo(() => [
    { field: "assetId", type: "dropDown", label: "Asset", api: "assets/for-dropdown?hasMeters=true", menuBarFilter: 1, displayField: (a) => `${a.unitNumber || a.assetNumber || ''} ${a.assetDescription || ''}` },
    { field: 'metersId', type: 'dropDown', label: 'Meter', api: 'meters', displayField: 'name', isMulti: false },
  ], []);

  const {
    appliedFilterParams,
    renderFilterControls,
    applyFilters
  } = useTableFilters(
    tableFilters,
    {},
    {
      onReset: () => setSearchTerm(''),
    },
  );

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  useEffect(() => {
    setReadings([]); setPage(1); setHasMore(true);
  }, [appliedFilterParams, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const resp = await getPaginatedMeterReadings({
          page,
          pageSize: 1000,
          search: searchTerm,
          sortField,
          sortOrder,
          ...appliedFilterParams,
          ...{ includeInvalid: 1 },
        });

        console.log('appliedFilterParams', appliedFilterParams);
        setReadings(prev => [...prev, ...resp.data]);
        setHasMore(page * 100 < resp.total);
      } catch (err) {
        console.error('Error fetching readings', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page, appliedFilterParams, searchTerm, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }

    setSortField(field);
    setSortOrder('ASC');
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5 && hasMore && !loading) {
        setPage(p => p + 1);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loading]);

  const meterOptionsForAsset = useMemo(() => {
    if (!newReading.assetId) return [];
    const allowedIds = assetMeters
      .filter(am => am.assetId === newReading.assetId)
      .map(am => am.meterId);
    return meterOptions.filter(m => allowedIds.includes(m.id));
  }, [newReading.assetId, assetMeters, meterOptions]);

  useEffect(() => {
    if (!newReading.assetId) {
      setNewReading(r => ({ ...r, metersId: null }));
      return;
    }
    const allowed = assetMeters
      .filter(am => am.assetId === newReading.assetId)
      .map(am => am.meterId);
    if (newReading.metersId && !allowed.includes(newReading.metersId)) {
      setNewReading(r => ({ ...r, metersId: null }));
    }
  }, [newReading.assetId, assetMeters]);

  const validateReading = async () => validateReadingEntry({
    newReading,
    assetId: newReading.assetId,
    meterId: newReading.metersId,
    assetMeters,
    setErrors,
    validateMeterReadingApi: validateMeterReading,
  });

  const openModal = (reading = null) => {
    setEditingId(reading?.id || null);
    setNewReading({
      assetId: reading?.assetId || null,
      metersId: reading?.metersId || null,
      readingDate: reading?.readingDate ? toLocalInputFormat(reading.readingDate) : nowLocalIso(),
      value: reading?.value ?? '',
    });
    setSelectedAssetOption(
      reading?.asset
        ? {
          value: reading.assetId,
          label: `${reading.asset.assetNumber} – ${reading.asset.assetDescription}`,
        }
        : null,
    );
    setErrors({});
    setShowModal(true);
  };

  const handleSaveReading = async () => handleSaveMeterReading({
    newReading,
    assetId: newReading.assetId,
    meterId: newReading.metersId,
    assetMeters,
    setErrors,
    validateMeterReadingApi: validateMeterReading,
    createReading: createMeterReadings,
    updateReading: updateMeterReadings,
    editingId,
    validateReadingFn: validateReading,
    onSuccess: async () => {
      setShowModal(false);
      // Clear the table data and trigger a refresh while
      // preserving the currently applied filters
      setReadings([]);
      setPage(1);
      setHasMore(true);
      // Re-apply the existing filters to force the data reload
      applyFilters();
    },
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reading?')) return;
    try {
      await deleteMeterReadings(id);
      setReadings(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error('delete', err);
    }
  };

  const generateCsv = () => {
    const headers = ['Date', 'Value', 'Meter', 'Asset'];
    const rows = readings.map((r) => [
      r.readingDate ? r.readingDate.slice(0, 16).replace('T', ' ') : '',
      r.value ?? '',
      r.meters?.name || '',
      r.asset ? `${r.asset.assetNumber} | ${r.asset.assetDescription}` : '',
    ]);
    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = ['Date', 'Value', 'Meter', 'Asset'];
    const rows = readings.map((r) => [
      r.readingDate ? r.readingDate.slice(0, 16).replace('T', ' ') : '',
      r.asset.assetStartDate ?? '',
      r.value ?? '',
      r.meters?.name || '',
      r.asset ? `${r.asset.assetNumber} | ${r.asset.assetDescription}` : '',
    ]);
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MeterReadings');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'meter-readings.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'meter-readings.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center p-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm btn-sm px-16 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
            {userProfile?.privileges?.includes('meter-readings:create') && (
              <button className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2" onClick={() => openModal()}>
                <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                Add Meter Reading
              </button>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <div className="paginated-table-container scroll-sm" ref={listRef} style={{ maxHeight: 'calc(100vh - 210px)' }}>
            <MeterReadingsDataTable
              readings={readings}
              onEdit={openModal}
              onDelete={handleDelete}
              loading={loading}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          </div>
        </div>
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Meter Reading' : 'Add Meter Reading'}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Asset *</label>
                  <AsyncSelect
                    classNamePrefix="react-select"
                    loadOptions={handleAssetSearch}
                    cacheOptions
                    defaultOptions={initialAssetOptions}
                    value={selectedAssetOption}
                    onChange={(opt) => {
                      setSelectedAssetOption(opt);
                      setNewReading({ ...newReading, assetId: opt ? opt.value : null });
                    }}
                    placeholder="Start typing to search ..."
                  />
                  {errors.assetId && <div className="text-danger small">{errors.assetId}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Meter *</label>
                  <Select
                    classNamePrefix="react-select"
                    value={meterOptionsForAsset
                      .map(m => ({ value: m.id, label: m.name }))
                      .find(opt => opt.value === newReading.metersId) || null}
                    onChange={(opt) => setNewReading({ ...newReading, metersId: opt ? opt.value : null })}
                    options={meterOptionsForAsset.map(m => ({ value: m.id, label: m.name }))}
                    isClearable
                    isDisabled={!newReading.assetId}
                  />
                  {errors.metersId && <div className="text-danger small">{errors.metersId}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Date / Time *</label>
                  <input
                    type="datetime-local"
                    className={`form-control ${errors.readingDate ? 'is-invalid' : ''}`}
                    value={newReading.readingDate || ''}
                    onChange={(e) => setNewReading({ ...newReading, readingDate: e.target.value })}
                  />
                  {errors.readingDate && <div className="invalid-feedback">{errors.readingDate}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Value *</label>
                  <input
                    type="number"
                    step="any"
                    className={`form-control ${errors.value ? 'is-invalid' : ''}`}
                    value={newReading.value || ''}
                    onChange={(e) => setNewReading({ ...newReading, value: e.target.value })}
                  />
                  {errors.value && <div className="invalid-feedback">{errors.value}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveReading}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default MeterReadingsLayer;
